import { useNavigate } from "react-router-dom";
import { MessageCircle, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatARS } from "@/lib/format";
import { waLink, cartMsg } from "@/lib/whatsapp";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, totals } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col border-l border-white/10 bg-panel p-0 sm:max-w-md">
        <SheetHeader className="border-b border-white/10 px-5 py-4">
          <SheetTitle className="font-display text-xl font-black uppercase tracking-wide text-white">
            Tu carrito <span className="text-volt">({totals.units})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" data-testid="cart-empty-state">
            <ShoppingBag className="h-10 w-10 text-zinc-700" />
            <p className="font-display text-xl font-bold uppercase text-zinc-400">Tu carrito está vacío</p>
            <p className="text-sm text-zinc-500">Sumá 2 prendas y llevate 10% OFF en la segunda.</p>
            <button
              data-testid="cart-empty-catalog-button"
              onClick={() => { setOpen(false); navigate("/tienda"); }}
              className="mt-2 bg-volt px-6 py-3 font-display text-sm font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim"
            >
              Ver catálogo
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 border border-white/10 bg-panel-2 p-3" data-testid={`cart-item-${i.productId}`}>
                  <img src={i.image} alt={i.name} className="h-20 w-20 shrink-0 object-cover" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="truncate font-display text-sm font-bold uppercase text-white">{i.name}</p>
                    {i.size && <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Talle {i.size}</p>}
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-white/15">
                        <button data-testid={`cart-qty-minus-${i.productId}`} onClick={() => updateQty(i.key, -1)} className="flex h-7 w-7 items-center justify-center text-zinc-400 hover:text-volt" aria-label="Restar"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-8 text-center font-mono text-sm text-white">{i.qty}</span>
                        <button data-testid={`cart-qty-plus-${i.productId}`} onClick={() => updateQty(i.key, 1)} className="flex h-7 w-7 items-center justify-center text-zinc-400 hover:text-volt" aria-label="Sumar"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <p className="font-mono text-sm font-bold text-white">{formatARS(i.price * i.qty)}</p>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${i.productId}`} onClick={() => removeItem(i.key)} className="self-start text-zinc-600 transition-colors hover:text-red-400" aria-label="Quitar"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>

            <div className="space-y-3 border-t border-white/10 px-5 py-4">
              {totals.units >= 2 ? (
                <div data-testid="discount-applied-banner" className="flex items-center justify-between bg-volt/10 px-3 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-volt">
                  <span>10% OFF 2da prenda aplicado</span>
                  <span>-{formatARS(totals.discount)}</span>
                </div>
              ) : (
                <div className="border border-dashed border-volt/40 px-3 py-2.5 text-center font-mono text-xs uppercase tracking-widest text-volt" data-testid="discount-hint-banner">
                  Sumá otra prenda y obtené 10% OFF
                </div>
              )}
              <div className="flex justify-between text-sm text-zinc-400">
                <span>Subtotal</span>
                <span className="font-mono">{formatARS(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between font-display text-xl font-black uppercase text-white">
                <span>Total</span>
                <span data-testid="cart-total" className="font-mono">{formatARS(totals.total)}</span>
              </div>
              <button
                data-testid="cart-checkout-mercadopago-button"
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="w-full bg-volt py-4 font-display text-base font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim"
              >
                Finalizar compra
              </button>
              <a
                href={waLink(cartMsg(items, totals))}
                target="_blank"
                rel="noreferrer"
                data-testid="cart-checkout-whatsapp-button"
                className="flex w-full items-center justify-center gap-2 border border-wa py-4 font-display text-base font-black uppercase tracking-widest text-wa transition-colors hover:bg-wa hover:text-black"
              >
                <MessageCircle className="h-5 w-5" /> Pedir por WhatsApp
              </a>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
